import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { BarChart3, TrendingUp, PieChart, Activity } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Skeleton from '../components/ui/Skeleton';
import { useAnalytics } from '../hooks/useTrends';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Analytics = () => {
  const { analytics, loading } = useAnalytics();

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
  };

  const lineChartData = {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [
      {
        label: 'Mentions',
        data: [12000, 19000, 15000, 25000, 22000, 30000, 28000],
        borderColor: '#ff4500',
        backgroundColor: 'rgba(255, 69, 0, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Engagement',
        data: [8000, 15000, 12000, 20000, 18000, 25000, 23000],
        borderColor: '#0079d3',
        backgroundColor: 'rgba(0, 121, 211, 0.1)',
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const barChartData = analytics ? {
    labels: analytics.topCategories.map(c => c.name),
    datasets: [
      {
        label: 'Trends by Category',
        data: analytics.topCategories.map(c => c.count),
        backgroundColor: [
          '#ff4500',
          '#0079d3',
          '#00a651',
          '#ff6a33',
          '#0066b3',
        ],
      },
    ],
  } : null;

  const doughnutData = analytics ? {
    labels: analytics.topRegions.map(r => r.name),
    datasets: [
      {
        data: analytics.topRegions.map(r => r.count),
        backgroundColor: [
          '#ff4500',
          '#0079d3',
          '#00a651',
          '#ff6a33',
          '#0066b3',
        ],
        borderWidth: 2,
        borderColor: '#ffffff',
      },
    ],
  } : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold mb-2">Analytics Dashboard</h1>
        <p className="text-reddit-600 dark:text-dark-textMuted">
          Comprehensive insights and trends analysis
        </p>
      </div>

      {/* Stats Cards */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="h-12 w-full mb-2" />
                <Skeleton className="h-4 w-2/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-reddit-600 dark:text-dark-textMuted">Total Trends</p>
                <TrendingUp className="w-5 h-5 text-reddit-orange" />
              </div>
              <p className="text-3xl font-bold mb-1">{analytics?.totalTrends.toLocaleString()}</p>
              <Badge variant="green" className="text-xs">+12% from last week</Badge>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-reddit-600 dark:text-dark-textMuted">Active Trends</p>
                <Activity className="w-5 h-5 text-reddit-blue" />
              </div>
              <p className="text-3xl font-bold mb-1">{analytics?.activeTrends.toLocaleString()}</p>
              <Badge variant="blue" className="text-xs">Real-time</Badge>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-reddit-600 dark:text-dark-textMuted">Total Mentions</p>
                <BarChart3 className="w-5 h-5 text-reddit-green" />
              </div>
              <p className="text-3xl font-bold mb-1">
                {(analytics?.totalMentions / 1000000).toFixed(1)}M
              </p>
              <Badge variant="green" className="text-xs">+8% from yesterday</Badge>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-reddit-600 dark:text-dark-textMuted">Avg Sentiment</p>
                <PieChart className="w-5 h-5 text-reddit-orange" />
              </div>
              <p className="text-3xl font-bold mb-1">
                {analytics?.avgSentiment > 0 ? '+' : ''}{analytics?.avgSentiment}
              </p>
              <Badge variant={analytics?.avgSentiment > 0 ? 'green' : 'orange'} className="text-xs">
                {analytics?.avgSentiment > 0 ? 'Positive' : 'Mixed'}
              </Badge>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-reddit-orange" />
              Weekly Trends
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <Line data={lineChartData} options={chartOptions} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-reddit-blue" />
              Categories Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              {loading || !barChartData ? (
                <Skeleton className="h-full w-full" />
              ) : (
                <Bar data={barChartData} options={chartOptions} />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5 text-reddit-green" />
              Regional Split
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              {loading || !doughnutData ? (
                <Skeleton className="h-full w-full" />
              ) : (
                <Doughnut data={doughnutData} options={chartOptions} />
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-reddit-orange" />
              Top Performing Topics
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {['AI Revolution', 'Climate Action', 'Space Technology', 'Web3 Innovation', 'Health Tech'].map((topic, idx) => (
                  <div key={idx} className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-reddit-100 dark:bg-dark-border font-bold text-reddit-orange">
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium">{topic}</span>
                        <span className="text-sm text-reddit-600 dark:text-dark-textMuted">
                          {(Math.random() * 100000 + 10000).toFixed(0)} mentions
                        </span>
                      </div>
                      <div className="w-full bg-reddit-100 dark:bg-dark-border rounded-full h-2">
                        <div
                          className="bg-reddit-orange h-2 rounded-full transition-all duration-500"
                          style={{ width: `${100 - idx * 15}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Analytics;